/**
 * 2026-08 brand palette.
 *
 * TRANSCRIBED from "Brand Identity Guidelines — Press Kit" pp. 9-10. DO NOT
 * REGENERATE. An earlier pass derived these ramps by scaling each core hex; it
 * produced plausible values that agreed with the official ramp at 500 and
 * nowhere else (derived 600 #4566A4 vs spec #4D72B7, derived 700 #2E446D vs
 * spec #3F5C94 — #2E446D is really 800). Steps the guidelines do not define are
 * marked DERIVED with the reason they exist.
 *
 * `sand` is the guidelines' Charcoal ramp plus Parchment, under the step
 * numbers this theme already referenced; `twilight` is Twilight verbatim.
 *
 *   --primary          #5C88DA  brand twilight (buttons, links, accents)
 *   --background       #F0EEE9  page background
 *   --foreground       #212721  headings / body
 *   --muted-foreground #565B56  secondary text
 *   --tertiary         #2E446D  deep navy, dark sections
 *   --border           #DBDCDB  hairlines
 *
 * ACCESSIBILITY — READ BEFORE CHANGING ANY OF THIS.
 *
 * Measured against the spec background (see scripts note below), the twilight
 * blue does NOT clear WCAG AA at body-text size:
 *
 *   #5C88DA on #F0EEE9 ................ 3.02:1  (AA normal text needs 4.5:1)
 *   white on #5C88DA .................. 3.51:1  (AA normal text needs 4.5:1)
 *
 * It passes as LARGE text (>=24px, 3:1) and as a non-text UI colour (3:1), so
 * the guidelines are not wrong — the colour is simply scoped to those uses.
 *
 * The palette already carries its own fix, which is almost certainly what
 * "more options for accessibility" on page 10 of the deck formalises:
 * `--tertiary` #2E446D is EXACTLY #5C88DA scaled to 50% brightness, and it
 * clears everything comfortably (8.37:1 on the cream, 9.70:1 under white).
 *
 * So the rule this file encodes:
 *
 *   twilight  → large display text, decorative fills, borders, focus rings,
 *               anything measured against the 3:1 non-text threshold
 *   navy      → body-size links, button fills that carry a white label, and
 *               any small text that has to be brand-coloured
 *
 * `twilight.600` is the 75% shade — the lightest step that still clears 4.5:1
 * in BOTH directions — kept as the hover/active partner for navy so the button
 * ramp moves toward the brand blue on interaction rather than away from it. It
 * is also the FOCUS RING: a keyboard focus indicator is held to 3:1 against
 * every surface it can be drawn on, and the twilight only clears that against
 * white (3.51:1) — on the cream chrome it falls to 2.32:1. See `border.focus`.
 *
 * Verified by theme/__tests__/brandContrast.test.ts, which recomputes every
 * pair above AND resolves the live semantic tokens out of `theme/index.ts`, so
 * a token repointed into the wrong role fails CI. Do not hand-edit a value here
 * without running it.
 */

/**
 * The six anchors, verbatim from the guidelines.
 *
 * @internal Test seam: brandContrast.test recomputes every anchor pair.
 */
export const BRAND_ANCHORS = {
  primary: '#5C88DA',
  background: '#F0EEE9',
  foreground: '#212721',
  mutedForeground: '#565B56',
  tertiary: '#2E446D',
  border: '#DBDCDB',
} as const;

/**
 * Twilight ramp, anchored on the spec primary at 500. Darker steps are the
 * brightness scale of the anchor, so 700 lands on the spec `--tertiary` navy
 * exactly — the ramp and the anchor agree rather than merely coexisting.
 */
const twilight = {
  50: '#F5F8FD', // spec
  100: '#E5ECF9', // spec
  200: '#CADAF3', // spec
  300: '#AEC4ED', // spec
  400: '#83A5E3', // spec
  500: '#5C88DA', // spec — CORE brand blue
  600: '#4D72B7', // spec — 4.11:1 as text (FAILS), 4.77:1 under a white label
  700: '#3F5C94', // spec — 5.71:1 on Parchment: smallest blue that carries text
  800: '#2E446D', // spec — the value Slack labelled "--tertiary"
  900: '#1D2C46', // spec
  950: '#131B2C', // DERIVED — no spec step below 900
};

/**
 * Warm neutral ramp, anchored on the spec background / border / ink.
 *
 * 500 is #7E817E rather than a smooth interpolation between 400 and 600: it is
 * the form-field border (`border.field`), and WCAG 2.2 1.4.11 holds a control's
 * outline to 3:1 against every surface it sits on. #7E817E is the lightest step
 * that clears it on all three — 3.94:1 on white, 3.40:1 on the cream canvas,
 * 3.07:1 on `sand.200`. The smooth value (#8A8D8A) reaches only 2.89:1 on the
 * cream, so it would have looked correct and failed. Pinned by
 * brandContrast.test.ts.
 */
const sand = {
  50: '#F7F6F3', // DERIVED — raised surface above Parchment
  100: '#F0EEE9', // ← spec --background
  200: '#E5E3DE',
  300: '#DBDCDB', // ← spec --border
  // The interaction ladder needs a press state one step below hover, and the
  // spec anchors leave a 1.42:1 gap between 300 and 400 — far too coarse for
  // it (sand.400 as `bg.active` drops secondary ink to 3.56:1 and squeezes the
  // focus ring to 2.92:1). 350 is that missing step: 1.12:1 from 300, which
  // matches the hover→active step the pre-2026-08 palette used.
  350: '#D0D1CE',
  400: '#B8BAB8', // spec — Charcoal 200
  500: '#7E817E', // border.field — see the 1.4.11 note above
  600: '#565B56', // ← spec --muted-foreground
  700: '#3F443F',
  800: '#2C312C',
  900: '#212721', // ← spec --foreground
  950: '#161B16', // spec — Charcoal 700
};

// ─── DESIGN.md families ─────────────────────────────────────────────────────
// Transcribed from DESIGN.md, which supersedes the Press Kit and adds the four
// families it never defined. Same rule as above: TRANSCRIBE, DO NOT DERIVE.

const bluebird = {
  50: '#F6FAFE',
  100: '#E7F3FB',
  200: '#CFE7F7',
  300: '#B4D9F3',
  400: '#8DC5ED',
  500: '#69B3E7',
  600: '#5896C2',
  700: '#477A9D',
  800: '#355A74',
  900: '#22394A',
};

const sunset = {
  50: '#FFFAF6',
  100: '#FFF2E6',
  200: '#FFE5CD',
  300: '#FFD7B1',
  400: '#FFC188',
  500: '#FFAE62',
  600: '#D69252',
  700: '#AD7643',
  800: '#805731',
  900: '#52381F',
};

const oxblood = {
  50: '#F8F4F5',
  100: '#EBE1E4',
  200: '#D7C3CA',
  300: '#C1A1AC',
  400: '#A06F81',
  500: '#824259',
  600: '#6D374B',
  700: '#582D3D',
  800: '#41212D',
  900: '#2A151C',
};

/** Success. */
const emerald = {
  50: '#F2FBF6',
  100: '#DDF5E7',
  200: '#BFEAD0',
  300: '#8DD9AC',
  400: '#56C382',
  500: '#2EA66A',
  600: '#207F50',
  700: '#17633F',
  800: '#10492F',
  900: '#0A2E1E',
};

/** Error / destructive. */
const poppy = {
  50: '#FFF5F5',
  100: '#FEE7E7',
  200: '#FACDCE',
  300: '#F5A9AC',
  400: '#E97A80',
  500: '#D9535A',
  600: '#B83C45',
  700: '#942F38',
  800: '#6E232B',
  900: '#46171D',
};

/**
 * Parchment is a SIX-step neutral (100-600), not a 50-900 ramp. The CANVAS is
 * 300 (#F8F7F4), not 500 (#F0EEE9) — 500 is what the Press Kit called "page
 * background", and DESIGN.md supersedes it. The lighter canvas raises every
 * token's contrast, so this is a net accessibility gain rather than a trade.
 */
const parchment = {
  100: '#FDFCFB',
  200: '#FAFAF8',
  300: '#F8F7F4',
  400: '#F4F2EE',
  500: '#F0EEE9',
  600: '#CAC8C4',
};

const sandstone = {
  50: '#FDFBFB',
  100: '#F9F6F3',
  200: '#F2ECE8',
  300: '#EBE2DB',
  400: '#E1D2C8',
  500: '#D7C4B7',
  600: '#B5A59A',
  700: '#92857C',
  800: '#6C625C',
  900: '#453F3B',
};

/** The true Charcoal ramp. `sand` above is the tuned UI ladder built from it. */
const charcoal = {
  50: '#F2F2F2',
  100: '#DBDCDB',
  200: '#B8BAB8',
  300: '#909390',
  400: '#565B56',
  500: '#212721',
  600: '#1C211C',
  700: '#161B16',
  800: '#111411',
  900: '#0B0C0B',
};

export const brandColors = {
  twilight,
  sand,
  bluebird,
  sunset,
  oxblood,
  emerald,
  poppy,
  parchment,
  sandstone,
  charcoal,
};

/**
 * Dark mode is DERIVED, not specified. The guidelines give one dark value
 * (`--tertiary`, for "dark sections"), which is a section treatment on a light
 * page, not a full dark theme. Everything below is an inference to be replaced
 * once page 10 lands:
 *   - canvas is a near-black warmed toward the sand hue, not the navy, because
 *     a navy canvas would leave the twilight blue with nowhere to sit;
 *   - twilight lifts on dark surfaces, where it can safely carry body-size
 *     links that it cannot carry on the cream.
 *
 * THIS IS THE ONLY COPY. `theme/index.ts` imports these values rather than
 * restating the hexes — an earlier cut kept both and they had already drifted
 * (`textMuted` here vs the shipped grey ink), so the constant documented one
 * palette while the app rendered another and the test pinned the unused half.
 * Add a value here and reference it there; never inline a dark hex.
 */
export const DARK_DERIVED = {
  canvas: '#151915',
  surface: '#1C211C',
  /**
   * The app frame's page fill — `frame.panel` and `sidebar.bg`, which are one
   * material and must stay equal in both modes.
   *
   * Its own step rather than `surface`, which it was until 2026-09. In LIGHT
   * the page is white and a box on it is parchment, so a box is a tint DOWN
   * from its page. In dark both were #1C211C, so every box drawn on the panel
   * — RowList, TableShell, the protocol section cards, the themed Kbd — was
   * the exact fill of the page and survived on its hairline alone. Lifting the
   * PAGE rather than dropping the boxes keeps that polarity, leaves every
   * `bg.surface` call site (128 outside tests on 2026-09-22, several of them
   * outside this frame and drawn on `bg.canvas`) untouched, and widens the gap
   * to the frame gutter so the curved panel reads more clearly, not less — the
   * `sm` shadow it otherwise leans on barely registers on a dark ground.
   *
   * WHAT IT COST, and what 2026-09-22 did about it. Everything drawn as an
   * EDGE on this page WAS `DARK_DERIVED.elevated` #242A24 — `border.muted`,
   * `border.subtle`, `sidebar.border` — and the page moved four values toward
   * it, so those hairlines went 1.115:1 to 1.063:1. A bordered box like
   * `TableShell` traded edge for fill: 1.115 -> 1.063 on the hairline while
   * the fill went 1.000 -> 1.049, two weak signals where there was one. This
   * comment used to end by deferring the remedy as "a separate decision with
   * its own before/after to look at".
   *
   * That decision was taken: David reported the tables and the dashboard's
   * `RowList` card as unreadable in dark, which is this paragraph's predicted
   * cost arriving as a bug report. `border.muted` and `border.subtle` now
   * point at `hairline` below, NOT at `elevated`, and read 1.223:1 on this
   * page, and `sidebar.border`, `table.border` and `card.border` were swept
   * with them. NOTHING draws an edge in `elevated` any more. `bg.elevated`
   * itself did not move — see its own comment for the two WCAG tests that
   * stopped it.
   */
  panel: '#202520',
  /**
   * The quiet tint — `bg.subtle` and `bg.muted`, which are ONE value in light
   * (`neutral.200` both) and are kept as one here.
   *
   * Its own step since 2026-09-22. It was `surface`, i.e. byte-identical to
   * the box it is drawn on, which made it invisible TWICE over. Resting: an
   * active filter chip in `SearchableMultiSelectMenu` was the same fill as a
   * resting one, recorded in `surfacePairs.test.tsx` as a `DOCUMENTS` case.
   * Hovering: `bg.subtle` is also the hover TARGET for 22 controls (counted
   * 2026-09-22) — `DataTable` rows, `ProtocolSection` headers,
   * `SettingsSidebarPrimitives`, `MemoryRow`, `SettingsSearchBox`,
   * `InlineEdit`, `WorkspaceSwitcherDropdown`, `SnapshotListItem`,
   * `LinkRow`, `DashedPillButton` (since deleted), `SegmentedControl` and
   * more — every one of which had NO hover feedback at all in dark while the handler ran and the
   * tests passed. That is rule 12's failure class, not a slight variant.
   * Separating this token is what revives all 22 at once, which is why the
   * fix is here and not at any call site: `ProtocolSection.tsx` carries an
   * explicit rationale for preferring `bg.subtle` over `bg.hover` on a row,
   * so repointing one call site would have split a single gesture across two
   * strengths and left that comment contradicting the tree.
   *
   * #252B25 puts it 1.130:1 above `surface` and 1.114:1 below `hover`, both
   * over the 1.10 interaction floor.
   *
   * WHAT THIS LIFT COSTS, because the paragraph above reads as though it were
   * free. `shadows.outline` — the app-wide keyboard focus ring — is
   * `rgba(77,114,183,0.95)` (accent.600) with NO `_dark` counterpart, so it is
   * the same light-tuned blue in both modes. Moving this token off `surface`
   * takes the ring from 3.232:1 to 2.865:1 against it, i.e. under 1.4.11's
   * 3:1, and NO choice of value here avoids that: the darkest step that still
   * clears the 1.10 floor above `surface` is #232923 and the ring reads 2.942
   * there. The ring already fails on `elevated` (2.903), `hover` and `active`
   * (2.597 / 2.158) — it is a light value doing dark duty, the same defect
   * class as the hairline below, and the fix is a dark ring rather than a
   * different tint. Out of scope here for the reason `elevated` is: it
   * restyles every focused control in the app. Pinned as a DOCUMENTS case in
   * `brandContrast.test.ts` and carried as a follow-up. `hover` and `active` moved up a step to
   * make that room: two 1.10 joints need 1.21 between `surface` and `hover`,
   * and the old #282E28 left 1.178. It sits 1.013:1 from `elevated`, which is
   * close but not a collision, but NOT for the reason an earlier draft of this
   * comment gave. It claimed "nothing renders one on the other", and that is
   * flatly false: `components.modal.ts` grounds every Modal and AlertDialog on
   * `bg.elevated`, and quiet panels are drawn inside those all over the app
   * (`ShareEntityDialog` alone has five).
   *
   * So measure it instead of asserting it. A `bg.subtle` panel on a modal goes
   * 1.1153:1 -> 1.0134:1 here, and that is the ONLY joint in the dark ramp
   * this change moves backwards. No value fixes it: `elevated` #242A24 sits
   * INSIDE the viable window for this token (#232923 -> #252B25 is the whole
   * range clearing the 1.10 floor above `surface`), so every candidate lands
   * within ~1.01 of it.
   *
   * It is accepted because the same change pays it back as an EDGE — but only
   * where an edge is actually drawn, and THREE successive drafts of this
   * paragraph claimed that more widely than was true. First "nothing renders
   * one on the other" (false: modals do). Then "every other quiet panel
   * already carries one" (false: 2 of 5 in `ShareEntityDialog` did). Then
   * "every modal panel" (false: `DestinationStep` and two `QuickCapturePage`
   * tap targets did not). Seven call sites were given an edge across those
   * rounds.
   *
   * WHY IT KEPT BEING WRONG, because that matters more than the count: a
   * quiet tint's ground is not decidable from the file it is written in.
   * `DestinationStep` is a wizard step whose Modal lives in a parent; a
   * file-level scan misses it. `SecurityTab` and `BirdMessage` hold an
   * elevated container somewhere and a `bg.subtle` element that is nowhere
   * near it; the same scan flags them falsely. Any claim here of the form
   * "every panel on an elevated ground" is unverifiable by grep, so this
   * comment does not make one. What is true: the sites found by two
   * independent sweeps now carry an edge.
   *
   * The class itself is unresolved and belongs to the theme, not to call
   * sites. A tint tuned as a step up from `bg.surface` cannot also be a step
   * away from `bg.elevated` while `elevated` sits inside its viable window.
   * Settling that needs either a token that means "quiet tint on THIS ground"
   * or a primitive that owns the recipe, which is the consolidation work.
   *
   * Where a border IS drawn, it carries the trade: `border.subtle` WAS
   * `elevated`, the identical #242A24, i.e. a 1.000:1 edge that did not exist,
   * and is now `hairline` at 1.1503:1 on that ground. Fill 1.115 with no edge
   * becomes fill 1.013 with a real edge, the same trade #2860 made in the
   * other direction.
   *
   * It IS byte-identical to `chromeActive` #252B25 below, and that is worth
   * saying rather than leaving for someone to find. A third ladder again:
   * `chromeActive` is the pressed step for the nav rail and workspace drawer,
   * measured from `chrome` #111511, and nothing paints a quiet tint on that
   * chrome. Recorded because two tokens that happen to be equal will be moved
   * independently one day, and the next person should know the equality was
   * observed rather than intended.
   */
  subtle: '#252B25',
  /**
   * Menus, popovers, drawers, modals — `bg.elevated`, and nothing else since
   * 2026-09-22: `sidebar.border` was the last hairline pointing here and moved
   * to `hairline` with the rest.
   *
   * STAYS at #242A24, and a failed attempt to lift it is worth recording. It
   * reads only 1.063:1 over the page it floats on since the 2026-09 panel
   * lift, so 2026-09-22 first tried #282E28 to win that back. Two theme tests
   * caught it: a brand badge composited on a menu fell to 4.42:1 against AA's
   * 4.5, and the selected fill to 2.915:1 against 1.4.11's 3.0. A menu is a
   * ground for ink and fills that are already near their floors, so it cannot
   * be lifted without re-tuning them — a separate change with its own
   * before/after. The menus keep their shadow and border meanwhile.
   */
  elevated: '#242A24',
  /**
   * The hairline — `border.muted` and `border.subtle`.
   *
   * Its own step since 2026-09-22, and the token this whole change is FOR.
   * These two pointed at `elevated`, a FILL value, which worked only while
   * the page sat at #1C211C. The 2026-09 panel lift moved the page to #202520
   * and took the hairline on it from 1.115:1 to 1.063:1 — so a `TableShell`
   * outline, the Protocol Library table's edge, simply stopped existing. That
   * commit measured the loss and deferred the fix to "a separate decision
   * with its own before/after to look at". This is that decision.
   *
   * A fill one step above the page cannot also be a hairline drawn ON the
   * page; the two jobs want opposite things from the same number. So the
   * hairline gets its own value and stops tracking `bg.elevated`.
   *
   * #2E342E reads 1.223:1 on the page and 1.283:1 on a box, against the
   * 1.283 / 1.197 LIGHT ships — the mirror is close, and inverted on purpose:
   * in light the box is DARKER than its page so a hairline reads harder on
   * the page, in dark it is lighter so it reads harder on the box.
   *
   * WHAT IT STILL COSTS. Drawn on a HOVERED row (#2C332C) it measures
   * 1.018:1 and is effectively gone. An earlier draft of this comment said
   * 1.030 and called it "not new", which was wrong twice: the number is
   * 1.018, and against the 1.056:1 it replaces that is the margin shrinking
   * by two thirds, not holding. Accepted because a hovered row is read by its
   * fill (1.260:1 up from the box) rather than its edge, the same argument
   * `active` makes below — but stated at its real size, because this is the
   * one number that argues the trade is cheap.
   */
  hairline: '#2E342E',
  /**
   * Resting -> HOVER, for anything drawn on `panel` above or on a `surface`
   * box, and shared with `sidebar.item.hover` so a nav row and the collapse
   * button beside it hover at the same strength.
   *
   * Its own step since the 2026-09 panel lift: `bg.hover` used to share
   * `elevated`, and lifting the page to #202520 left a ghost control resting
   * ON it hovering at 1.063:1 where it had been 1.115:1.
   *
   * #2C332C since 2026-09-22, up from the #282E28 that lift chose, and the
   * reason is `subtle` above rather than anything wrong with #282E28 on its
   * own. Separating `bg.subtle` from `bg.surface` inserts a rung BETWEEN the
   * box and this one, and two 1.10 joints need 1.21 of room where #282E28
   * left 1.178 — so the tint could not clear `surface` and this step at once
   * until this one moved up. It now reads 1.201:1 on the page, 1.260:1 on a
   * box, and 1.114:1 above the tint. `active` moves with it — see below.
   */
  hover: '#2C332C',
  /**
   * Pressed / selected rows — one step above `hover`, and it moves whenever
   * `hover` does. The 2026-09 lift already proved why: left behind once, it
   * sat 1.070:1 above the new hover, WORSE than the 1.063 regression that
   * lift existed to fix, and every test stayed green because three distinct
   * values still satisfy the "three distinct steps" rule. Both joints are
   * floored in `surfacePairs.test.tsx`.
   *
   * #3A403A, tracking `hover`'s move to #2C332C at 1.221:1 above it.
   *
   * THE CONSTRAINT THAT PICKED IT is not that step, though — it is
   * `border.default` #343A34, which a hairline on a pressed row is drawn in.
   * The previous #303630 sat just UNDER that value and deliberately kept
   * 1.062:1 off it. Tracking the hover lift naively to #363C36 moves `active`
   * straight PAST #343A34 and lands 1.031:1 away on the other side — a joint
   * that gets worse while every "three distinct steps" and not-equal check in
   * the theme stays green, which is the same silent-compression failure this
   * comment has now recorded twice. #3A403A clears it to 1.096:1, better than
   * the 1.062 it replaces, and is floored by
   * `a hairline on a pressed row` in surfacePairs.test.tsx so the next lift
   * cannot repeat it.
   */
  active: '#3A403A',
  border: '#343A34',
  /** Chrome (nav rail, workspace drawer) — one step BELOW the canvas. */
  chrome: '#111511',
  chromeHover: '#1C211C',
  chromeActive: '#252B25',
  text: '#F0EEE9',
  /**
   * #B4B9B4, not the #A8ADA8 a straight read off the sand ramp gives. Both
   * clear AA on every dark surface in the theme (#A8ADA8 measures 6.43:1 on
   * `elevated`), so this is a legibility preference rather than a fix — the
   * lighter ink holds up better against the warmed surfaces at small sizes.
   */
  textMuted: '#B4B9B4',
  /**
   * Brand-as-foreground on a dark surface. `twilight.300`, not 400: the warmed
   * dark surfaces are lighter than a neutral near-black, and 400 lands at
   * 4.19:1 on `elevated`. Pinned by contrast.test.ts.
   */
  brandOnSurface: twilight[300],
  /** Brand-as-link. Body-size, so it also has to clear 4.5:1. */
  brandLink: twilight[400],
} as const;
